import { Injectable, inject } from '@angular/core';
import { SelectItem, TableDataPayload, TableLazyLoad } from '@web/shared/ui';
import { map, Observable } from 'rxjs';
import { CollectionRequestBuilder } from '@web/common';
import { WarehouseOwnerCollectionItem } from '../models/warehouse-owner-collection-item';
import { WarehouseOwnerApiService } from '../api/services/warehouse-owner-api.service';
import { WarehouseOwnerCollectionFilter } from '../models/warehouse-owner-collection-filter';
import { Guid } from '@web/utils';
import { WarehouseOwnerDetails } from '../models/warehouse-owner-details';

@Injectable({
  providedIn: 'root',
})
export class WarehouseOwnerDataSourceService {
  private readonly warehouseOwnerApi = inject(WarehouseOwnerApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);

  public fetchCollection(
    lazyLoad: TableLazyLoad<WarehouseOwnerCollectionFilter>,
  ): Observable<TableDataPayload<WarehouseOwnerCollectionItem>> {
    return this.warehouseOwnerApi
      .fetchCollection(
        this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('label').build(),
              )
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .buildAsArray(),
          )
          .build(),
      )
      .pipe(
        map(
          (response) =>
            new TableDataPayload<WarehouseOwnerCollectionItem>(
              response.items.map(
                (item) =>
                  new WarehouseOwnerCollectionItem({
                    id: item.id,
                    slug: item.slug,
                    label: item.label,
                    createdAt: item.createdAt,
                    canBeEdited: item.contextOperations.canBeEdited,
                    canBeRemoved: item.contextOperations.canBeDeleted,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public fetchDetailsById(
    id: Guid,
    cacheable = false,
  ): Observable<WarehouseOwnerDetails> {
    return this.warehouseOwnerApi.fetchDetailsById(id, cacheable).pipe(
      map(
        (response) =>
          new WarehouseOwnerDetails({
            id: response.id,
            slug: response.slug,
            label: response.label,
            createdAt: response.createdAt,
            canBeEdited: response.contextOperations.canBeEdited,
            canBeRemoved: response.contextOperations.canBeDeleted,
          }),
      ),
    );
  }

  public fetchAll(): Observable<WarehouseOwnerDetails[]> {
    return this.warehouseOwnerApi
      .fetchCollection(
        this.collectionRequestBuilder.disablePagination().build(),
        true,
      )
      .pipe(
        map((response) =>
          response.items.map(
            (item) =>
              new WarehouseOwnerDetails({
                id: item.id,
                slug: item.slug,
                label: item.label,
                createdAt: item.createdAt,
                canBeEdited: item.contextOperations.canBeEdited,
                canBeRemoved: item.contextOperations.canBeDeleted,
              }),
          ),
        ),
      );
  }

  public fetchCollectionAsSelectItems(): Observable<SelectItem<Guid>[]> {
    return this.warehouseOwnerApi
      .fetchCollection(
        this.collectionRequestBuilder.disablePagination().build(),
        true,
      )
      .pipe(
        map((response) =>
          response.items.map((item) => new SelectItem(item.label, item.id)),
        ),
      );
  }
}
